import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { postRequest } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  EmailOutlined,
} from '@mui/icons-material';

export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Convert email to lowercase before sending
      const formattedData = {
        ...data,
        email: data.email.toLowerCase()
      };
      const response = await postRequest('auth/forgotPassword', formattedData);
      console.log(response, 'FORGOT_PASSWORD_RESPONSE');

    } catch (err) {
      //toast.error(err?.message || 'Failed to send reset link');
      console.error('Forgot Password Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '150%',
          height: '150%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'moveBackground 20s linear infinite',
        },
        '@keyframes moveBackground': {
          '0%': { transform: 'translate(0, 0)' },
          '100%': { transform: 'translate(50px, 50px)' },
        },
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              animation: 'fadeInUp 0.6s ease-out',
              '@keyframes fadeInUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(30px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {/* Logo Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              {/* <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                  fontFamily: 'Poppins',
                  letterSpacing: '0.05em',
                  animation: 'fadeInDown 0.8s ease-out',
                  '@keyframes fadeInDown': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(-20px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                UNIVERSAL
              </Typography> */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#333',
                  mb: 1,
                  fontFamily: 'Poppins',
                }}
              >
                Forgot Password?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Enter your email and we'll send you a reset link
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Email Field */}
              <TextField
                fullWidth
                label="Email Address"
                margin="normal"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address',
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={loading}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ color: '#764ba2' }} />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(118, 75, 162, 0.15)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 4px 12px rgba(118, 75, 162, 0.25)',
                    },
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#764ba2',
                    borderWidth: '2px',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#764ba2',
                  },
                }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.8,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 4px 15px rgba(118, 75, 162, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 20%, #667eea 100%)',
                    boxShadow: '0 6px 20px rgba(118, 75, 162, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    opacity: 0.6,
                  },
                }}
                startIcon={loading && <CircularProgress size={20} sx={{ color: 'white' }} />}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              {/* Back to Login */}
              <Box display="flex" justifyContent="center" mt={3}>
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => navigate('/login')}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    color: '#764ba2',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#667eea',
                      backgroundColor: 'rgba(118, 75, 162, 0.08)',
                    },
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            </Box>

            {/* Footer */}
            {/* <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Protected by Universal Security
              </Typography>
            </Box> */}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}
